import { CrmNoteType, CrmTaskStatus } from '@plato/data/prisma';
import { CustomerBusinessStatus } from '@plato/data/starrocks/types';
import type { FilterMenuCategory, FilterMenuSelection } from '@plato/ui/components/filter-menu';
import { useTranslation } from 'react-i18next';
import {
    ActivityFeedModelType,
    InsightOutcomeResponseFilterOptions,
    OrderStatusFilterOptions,
    QuoteStatusFilterOptions,
} from '../../../server/types/customer-activity-feed-types.ts';
import { api } from '../../../utils/api.ts';
import { getFullName } from '../../../utils/name.ts';
import {
    useCustomerStatusFilter,
    useInsightOutcomeFilter,
    useNoteTypeFilter,
    useOrderStatusFilter,
    useQuoteStatusFilter,
    useTaskStatusFilter,
    type ActivityFilterOption,
    type ActivityQueryFilters,
} from '../../activity-feed/hooks/use-activity-filters.ts';

/** The activity types offered, in the order the design lists them. */
const modelTypeOrder = [
    ActivityFeedModelType.TASK,
    ActivityFeedModelType.NOTE,
    ActivityFeedModelType.QUOTE,
    ActivityFeedModelType.ORDER,
    ActivityFeedModelType.INSIGHT_OUTCOME,
    ActivityFeedModelType.CUSTOMER_STATUS_CHANGE,
];

/** The global feed lists no quotes or orders, so it offers neither the types nor their statuses. */
const customerOnlyModelTypes: ActivityFeedModelType[] = [
    ActivityFeedModelType.QUOTE,
    ActivityFeedModelType.ORDER,
];
const customerOnlyCategoryKeys = ['quoteStatuses', 'orderStatuses'];

export type ActivityFilterCategoryOptions = {
    scope: 'customer' | 'global';
    /** Off when the feed is pinned to the signed-in user ("My Activity"). */
    showUserFilter: boolean;
};

function pickAllowed<T extends string>(allowed: Record<string, T>, values: string[] | undefined): T[] {
    return Object.values(allowed).filter((value) => values?.includes(value));
}

export function toFilterMenuSelection(filters: ActivityQueryFilters): FilterMenuSelection {
    return {
        modelType: filters.modelTypes ?? (filters.modelType ? [filters.modelType] : []),
        taskStatuses: filters.taskStatuses ?? [],
        noteTypes: filters.noteTypes ?? [],
        quoteStatuses: filters.quoteStatuses ?? [],
        orderStatuses: filters.orderStatuses ?? [],
        insightOutcomeResponses: filters.insightOutcomeResponses ?? [],
        customerStatuses: filters.customerStatuses ?? [],
        userIds: filters.userIds ?? [],
    };
}

export function toActivityFilters(selection: FilterMenuSelection): ActivityQueryFilters {
    return {
        // the menu always writes the list, so a single type from an old link is dropped
        modelType: undefined,
        modelTypes: pickAllowed(ActivityFeedModelType, selection.modelType),
        taskStatuses: pickAllowed(CrmTaskStatus, selection.taskStatuses),
        noteTypes: pickAllowed(CrmNoteType, selection.noteTypes),
        quoteStatuses: pickAllowed(QuoteStatusFilterOptions, selection.quoteStatuses),
        orderStatuses: pickAllowed(OrderStatusFilterOptions, selection.orderStatuses),
        insightOutcomeResponses: pickAllowed(
            InsightOutcomeResponseFilterOptions,
            selection.insightOutcomeResponses,
        ),
        customerStatuses: pickAllowed(CustomerBusinessStatus, selection.customerStatuses),
        userIds: selection.userIds ?? [],
    };
}

function toCategory(filter: ActivityFilterOption | undefined): FilterMenuCategory[] {
    if (filter?.type !== 'checkbox') {
        return [];
    }

    return [
        {
            key: filter.id,
            label: filter.label,
            options: filter.options.map((option) => ({ value: option.value, label: option.label })),
        },
    ];
}

/**
 * The filter menu's categories. Keys are the filter fields themselves, so a
 * `FilterMenuSelection` maps straight onto `ActivityQueryFilters`.
 */
export function useActivityFilterCategories({
    scope,
    showUserFilter,
}: ActivityFilterCategoryOptions): FilterMenuCategory[] {
    const { t } = useTranslation('customer-detail');
    const { data: users } = api.platoUser.query.users.getManyUsers.getSubordinates.useQuery();

    const statusCategories = [
        useTaskStatusFilter(),
        useNoteTypeFilter(),
        useQuoteStatusFilter(),
        useOrderStatusFilter(),
        useInsightOutcomeFilter(),
        useCustomerStatusFilter(),
    ]
        .flatMap(toCategory)
        .filter((category) => scope === 'customer' || !customerOnlyCategoryKeys.includes(category.key));

    const modelTypeLabels: Record<ActivityFeedModelType, string> = {
        [ActivityFeedModelType.QUOTE]: t('customer_detail__quotes'),
        [ActivityFeedModelType.ORDER]: t('customer_detail__orders'),
        [ActivityFeedModelType.NOTE]: t('customer_detail__notes'),
        [ActivityFeedModelType.TASK]: t('customer_detail__tasks'),
        [ActivityFeedModelType.INSIGHT_OUTCOME]: t('customer_detail__insight_outcomes'),
        [ActivityFeedModelType.CUSTOMER_STATUS_CHANGE]: t('customer_detail__status_changes'),
    };

    const categories: FilterMenuCategory[] = [
        {
            key: 'modelType',
            label: t('filter__category_type', { ns: 'activity-feed' }),
            options: modelTypeOrder
                .filter((modelType) => scope === 'customer' || !customerOnlyModelTypes.includes(modelType))
                .map((modelType) => ({
                    value: modelType,
                    label: modelTypeLabels[modelType],
                })),
        },
        ...statusCategories,
    ];

    // Only worth a category when there is somebody else to filter by.
    if (showUserFilter && users && users.length > 1) {
        categories.push({
            key: 'userIds',
            label: t('activity_feed__filter_users'),
            options: users.map((user) => ({
                value: user.id,
                label: getFullName(user.firstName, user.lastName),
            })),
        });
    }

    return categories;
}
